import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Alert, BeforeAfter, FunctionDef, IntentSummary, MacroScope, RunSummary, Scope, ScopeCandidate, WorkType } from "../types";
import { api } from "../api";
import { fmtSigned, fmtUnits, scopeLabel, STATUS_LABEL } from "../format";
import { LineChart } from "./charts";
import { inMacro } from "./TopFilters";
import InlineComposer, { type ComposerSeed } from "./InlineComposer";
import { AnswerPanel, Suggestions } from "./AnswerView";
import { IconButton } from "./Icons";
import SummaryText from "./RunSummary";
import MetricsBar from "./MetricsBar";
import { AlertStack, BellIcon } from "./Alerts";

// ── layout ──────────────────────────────────────────────────────────────────
// Time runs left → right: Older, Last week, Yesterday, then a wide Today. Past buckets are
// shaded bands running the full height of the canvas; Today is the open working area.
const CARD_W = 380;   // same width as the details panel that opens under a card
const CARD_H = 136;
const CARD_GAP = 20;
const PER_COL = 4;
const COL_GAP = 26;     // between columns of the same day
const DAY_GAP = 44;     // between days in a bucket
const BUCKET_GAP = 96;  // soft gap between buckets; the cloudy band edges fade into it
const BUCKET_PAD = 34;
const BUCKET_HEAD = 58;
const DAY_HEAD = 26;
const TODAY_W = 2200;   // Today is wide so it fills the middle and right of the screen
const BAND_EXT = 4000;  // bands extend this far above and below the cards
const DRAG_SLACK = CARD_W + 2 * COL_GAP;
const COMPOSER_W = 264;  // the entry box is card-sized
const ALERT_STRIP = 54;  // room above the cards for a bucket's alert stack (only when there are alerts)
const ALERT_PILL_W = 300;

type BucketId = "older" | "lastweek" | "yesterday" | "today";
const BUCKETS: { id: BucketId; label: string }[] = [
  { id: "older", label: "Older" },
  { id: "lastweek", label: "Last week" },
  { id: "yesterday", label: "Yesterday" },
  { id: "today", label: "Today" },
];

interface Box { x: number; y: number; w: number; h: number }
interface DayLayout { key: string; label: string; x: number; y: number }
interface BucketLayout { id: BucketId; label: string; box: Box; days: DayLayout[] }
/** origin: the top-left of the card's day column. Saved positions are offsets from it. */
interface CardLayout { intent: IntentSummary; box: Box; origin: { x: number; y: number }; minX: number; maxX: number; minY: number }
interface Layout { buckets: BucketLayout[]; cards: CardLayout[]; bounds: Box; today: Box; nextSlot: { x: number; y: number }; head: number }

const DAY_MS = 86_400_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function bucketOf(created: Date, today: Date): BucketId {
  const days = Math.round((today.getTime() - startOfDay(created).getTime()) / DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days <= 7) return "lastweek";
  return "older";
}

/** Small, stable offsets per card so the canvas reads as free-flowing, not a grid. */
function jitter(id: string): { dx: number; dy: number } {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return { dx: ((h & 0xff) / 255 - 0.5) * 16, dy: (((h >>> 8) & 0xff) / 255) * 18 };
}

function placed(slot: Box, origin: { x: number; y: number }, pos: IntentSummary["pos"],
                minX: number, maxX: number, minY: number): Box {
  if (!pos) return slot;
  return { ...slot, x: Math.min(maxX, Math.max(minX, origin.x + pos.dx)), y: Math.max(minY, origin.y + pos.dy) };
}

/**
 * Keep cards apart: going top to bottom, a card that overlaps one above it (a collapsed card
 * expanded, a card placed by hand on another) is drawn just below it instead. Only where it is
 * drawn changes; its saved position stays, so it returns there once there's room again.
 */
function separate<T extends { box: Box }>(items: T[]): T[] {
  const order = [...items].sort((a, b) => a.box.y - b.box.y || a.box.x - b.box.x);
  const settled: Box[] = [];
  for (const it of order) {
    const b = { ...it.box };
    let moved = true;
    while (moved) {
      moved = false;
      for (const s of settled) {
        const overlapX = b.x < s.x + s.w && s.x < b.x + b.w;
        const overlapY = b.y < s.y + s.h + CARD_GAP && s.y < b.y + b.h;
        if (overlapX && overlapY) { b.y = s.y + s.h + CARD_GAP; moved = true; }
      }
    }
    it.box = b;
    settled.push(b);
  }
  return order;
}

function computeLayout(intents: IntentSummary[], now: Date, heightOf: (i: IntentSummary) => number,
                       alertBuckets: Set<BucketId> = new Set()): Layout {
  const today = startOfDay(now);
  // Cards start lower when any bucket shows alerts, so the stacks line up along the top.
  const head = BUCKET_HEAD + (alertBuckets.size ? ALERT_STRIP : 0);
  const grouped = new Map<BucketId, Map<string, IntentSummary[]>>();
  for (const it of intents) {
    const created = new Date(it.created_at);
    const b = bucketOf(created, today);
    const days = grouped.get(b) ?? new Map<string, IntentSummary[]>();
    const k = dayKey(created);
    days.set(k, [...(days.get(k) ?? []), it]);
    grouped.set(b, days);
  }

  const cards: CardLayout[] = [];
  const buckets: BucketLayout[] = [];
  let x = 0;
  let maxH = 0;
  let nextSlot = { x: 0, y: head };

  for (const { id, label } of BUCKETS) {
    const days = grouped.get(id);
    if (!days && id !== "today" && !alertBuckets.has(id)) continue;
    const bx = x;
    const bucketCards: { intent: IntentSummary; slot: Box; origin: { x: number; y: number } }[] = [];
    let cx = bx + BUCKET_PAD;
    let colH = 0;
    const dayLayouts: DayLayout[] = [];
    const dayKeys = [...(days?.keys() ?? [])].sort();
    if (id === "today" && !dayKeys.length) dayKeys.push(dayKey(today));
    const labelled = id === "older" || id === "lastweek";
    dayKeys.forEach((k, di) => {
      const list = (days?.get(k) ?? []).slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
      const top = head + (labelled ? DAY_HEAD : 0);
      if (labelled) {
        const d = new Date(`${k}T12:00:00`);
        dayLayouts.push({ key: k, x: cx, y: head - 4,
          label: d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) });
      }
      // Cards placed by hand don't take a grid slot.
      const origin = { x: cx, y: top };
      const auto = list.filter((i) => !i.pos);
      list.filter((i) => i.pos).forEach((intent) =>
        bucketCards.push({ intent, origin, slot: { x: cx, y: top, w: CARD_W, h: heightOf(intent) } }));
      // Auto-placed cards stack down a column (by their own height), PER_COL to a column.
      let yCursor = 0;
      auto.forEach((intent, n) => {
        const col = Math.floor(n / PER_COL);
        if (n % PER_COL === 0) yCursor = top + ((col + di) % 2 ? 26 : 0);
        const j = jitter(intent.id);
        const h = heightOf(intent);
        bucketCards.push({ intent, origin, slot: {
          x: cx + col * (CARD_W + COL_GAP) + j.dx, y: yCursor + j.dy, w: CARD_W, h,
        } });
        yCursor += h + CARD_GAP;
      });
      const cols = Math.max(1, Math.ceil(auto.length / PER_COL));
      if (id === "today") {
        const col = Math.floor(auto.length / PER_COL);
        nextSlot = { x: cx + col * (CARD_W + COL_GAP), y: auto.length % PER_COL ? yCursor : top };
      }
      cx += cols * (CARD_W + COL_GAP) - COL_GAP;
      if (di < dayKeys.length - 1) cx += DAY_GAP;
    });
    let w = cx - bx + BUCKET_PAD;
    if (id === "today") w = Math.max(w, TODAY_W);
    if (alertBuckets.has(id)) w = Math.max(w, ALERT_PILL_W + 2 * BUCKET_PAD);
    const minX = bx + 8;
    const maxX = id === "today" ? bx + w - CARD_W - 8 : bx + w - CARD_W - 8 + DRAG_SLACK;
    const minY = head;
    const placedCards = separate(bucketCards.map(({ intent, slot, origin }) =>
      ({ intent, origin, box: placed(slot, origin, intent.pos, minX, maxX, minY) })));
    for (const { intent, box, origin } of placedCards) {
      cards.push({ intent, box, origin, minX, maxX, minY });
      colH = Math.max(colH, box.y + box.h);
      w = Math.max(w, box.x + CARD_W + BUCKET_PAD - bx);
    }
    buckets.push({ id, label, box: { x: bx, y: -BAND_EXT, w, h: 0 }, days: dayLayouts });
    maxH = Math.max(maxH, colH + BUCKET_PAD);
    x = bx + w + BUCKET_GAP;
  }
  for (const b of buckets) b.box.h = maxH + 2 * BAND_EXT;
  cards.sort((a, b) => Number(!!a.intent.pos) - Number(!!b.intent.pos));
  const todayBox = buckets.find((b) => b.id === "today")!.box;
  return { buckets, cards, nextSlot, head, today: { ...todayBox, y: 0, h: maxH },
           bounds: { x: 0, y: 0, w: x - BUCKET_GAP, h: Math.max(maxH, 600) } };
}

// ── view state ──────────────────────────────────────────────────────────────
interface View { x: number; y: number; k: number }
const viewKey = (boardId: string) => `gentik.view3.${boardId}`;
function loadView(boardId: string): View | null {
  try {
    const v = JSON.parse(localStorage.getItem(viewKey(boardId)) || "null");
    return v && typeof v.k === "number" ? v : null;
  } catch { return null; }
}
function storeView(boardId: string, v: View) {
  try { localStorage.setItem(viewKey(boardId), JSON.stringify(v)); } catch { /* ignore */ }
}
const clampK = (k: number) => Math.min(2, Math.max(0.2, k));
const TOP_INSET = 124; // room for the metrics bar (10px offset + 104px high + gap)

const collapsedKey = (boardId: string) => `gentik.collapsed.${boardId}`;
function loadCollapsed(boardId: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(collapsedKey(boardId)) || "[]")); } catch { return new Set(); }
}
function storeCollapsed(boardId: string, ids: Set<string>) {
  try { localStorage.setItem(collapsedKey(boardId), JSON.stringify([...ids])); } catch { /* ignore */ }
}
const COLLAPSED_H = 34;
const ASKING_H = 250;
const ANSWER_EXTRA = 62;    // a question card shows the start of its answer
const SUGGESTION_H = 30;    // per follow-up intent offered under an answer
const FORK_GAP = 70;        // a fork's composer opens this far to the right of its question
const STAGED_EXTRA = 30; // room for the countdown / push / discard row   // a card asking "which products did you mean?"

export interface ComposeRequest { seed: ComposerSeed; nonce: number }

interface Props {
  boardId: string;
  intents: IntentSummary[];
  workTypes: Record<string, WorkType>;
  functions: FunctionDef[];
  macro: MacroScope;
  scenario: string;
  selectedIntent: string | null;
  composeRequest: ComposeRequest | null;
  onOpenIntent: (id: string) => void;
  onMoveIntent: (id: string, pos: { dx: number; dy: number }) => void;
  /** fork: the new intent comes from another card (a question's "Act on this") and keeps its scope. */
  onCreateIntent: (text: string, run: boolean, fork?: { parentId: string; scope: Scope },
                   pos?: { dx: number; dy: number } | null) => Promise<IntentSummary>;
  /** Several intents at once (pasted rows): run = start them all, else drafts. */
  onCreateIntents: (texts: string[], run: boolean, positions?: { dx: number; dy: number }[]) => Promise<IntentSummary[]>;
  /** Reload the cards after an action on one (confirm scope, push, discard). */
  onRefresh: () => void;
  /** Alerts still tracked (not resolved): stacked at the top of the bucket they were detected in. */
  alerts: Alert[];
  onAlertsChanged: () => void;
  notify: (msg: string) => void;
}

export default function Canvas(props: Props) {
  const { boardId, intents, workTypes, functions, macro, selectedIntent, composeRequest,
          onOpenIntent, onMoveIntent, onCreateIntent, onCreateIntents, onRefresh, alerts, onAlertsChanged, notify } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(() => loadView(boardId) ?? { x: 40, y: TOP_INSET, k: 0.85 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const [panning, setPanning] = useState(false);
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number } | null>(null);
  // Dragging a batch header moves every card of the batch together.
  const [batchDrag, setBatchDrag] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const justDragged = useRef<string | null>(null);
  const [composer, setComposer] = useState<{ x: number; y: number; seed: ComposerSeed } | null>(null);
  const pendingPlace = useRef<{ id: string; x: number; y: number }[]>([]);
  const [now, setNow] = useState(() => new Date());
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(boardId));
  const [details, setDetails] = useState<string | null>(null);
  const toggleCollapsed = useCallback((id: string) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      storeCollapsed(boardId, next);
      return next;
    });
    setDetails((d) => (d === id ? null : d));
  }, [boardId]);
  const [openStack, setOpenStack] = useState<BucketId | null>(null);
  const alertsByBucket = useMemo(() => {
    const m = new Map<BucketId, Alert[]>();
    for (const a of alerts) {
      const b = bucketOf(new Date(a.detected_at), startOfDay(now));
      m.set(b, [...(m.get(b) ?? []), a]);
    }
    return m;
  }, [alerts, now]);
  const alertBuckets = useMemo(() => new Set(alertsByBucket.keys()), [alertsByBucket]);
  const layout = useMemo(
    () => computeLayout(intents, now, (i) => (i.status === "needs_confirmation" ? ASKING_H
      : collapsed.has(i.id) ? COLLAPSED_H : i.status === "staged" ? CARD_H + STAGED_EXTRA
      : isAnswered(i) ? CARD_H + ANSWER_EXTRA + Math.min(3, i.last_run?.suggestions?.length ?? 0) * SUGGESTION_H : CARD_H),
      alertBuckets),
    [intents, now, collapsed, alertBuckets]);

  // Re-bucket after midnight.
  useEffect(() => {
    const id = window.setInterval(() => setNow((cur) => (dayKey(cur) === dayKey(new Date()) ? cur : new Date())), 60_000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => { storeView(boardId, view); }, [boardId, view]);

  // New cards land where their composer was opened (a batch: stacked in one column below it).
  useEffect(() => {
    if (!pendingPlace.current.length) return;
    const waiting: typeof pendingPlace.current = [];
    for (const p of pendingPlace.current) {
      const c = layout.cards.find((card) => card.intent.id === p.id);
      if (!c) { waiting.push(p); continue; }
      if (!c.intent.pos) onMoveIntent(p.id, { dx: Math.round(p.x - c.origin.x), dy: Math.round(p.y - c.origin.y) });
    }
    pendingPlace.current = waiting;
  }, [layout, onMoveIntent]);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const w = layout.today.x + Math.min(layout.today.w, 1200);
    const h = layout.bounds.h;
    const k = clampK(Math.min(1, (el.clientWidth - 80) / Math.max(w, 1), (el.clientHeight - TOP_INSET - 40) / Math.max(h, 1)));
    setView({ k, x: 40, y: TOP_INSET });
  }, [layout]);

  /** Today fills the middle and right; the past stays visible on the left third. */
  const focusToday = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const k = 0.85;
    setView({ k, x: el.clientWidth * 0.3 - layout.today.x * k, y: TOP_INSET });
  }, [layout.today.x]);

  useLayoutEffect(() => { if (!loadView(boardId)) focusToday(); }, [boardId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Pan just enough that a world-space box is on screen. */
  const reveal = useCallback((b: Box) => {
    const el = ref.current;
    if (!el) return;
    setView((v) => {
      const left = v.x + b.x * v.k, right = v.x + (b.x + b.w) * v.k;
      const top = v.y + b.y * v.k, bottom = v.y + (b.y + b.h) * v.k;
      let { x, y } = v;
      if (right > el.clientWidth - 20) x -= right - (el.clientWidth - 20);
      if (left + (x - v.x) < 20) x += 20 - (left + (x - v.x));
      if (bottom > el.clientHeight - 60) y -= bottom - (el.clientHeight - 60);
      if (top + (y - v.y) < TOP_INSET) y += TOP_INSET - (top + (y - v.y));
      return { ...v, x, y };
    });
  }, []);

  const openComposer = useCallback((x: number, y: number, seed: ComposerSeed) => {
    const t = layout.today;
    const cx = Math.min(Math.max(x, t.x + 8), t.x + t.w - COMPOSER_W - 8);
    const cy = Math.max(y, layout.head);
    setComposer({ x: cx, y: cy, seed });
    reveal({ x: cx, y: cy, w: COMPOSER_W, h: 120 });
  }, [layout.today, layout.head, reveal]);

  // Requests from outside the canvas (top-bar button, "Reuse" in the drawer).
  useEffect(() => {
    if (composeRequest) openComposer(layout.nextSlot.x, layout.nextSlot.y, composeRequest.seed);
  }, [composeRequest?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wheel / pinch zooms at the pointer. Moving around is drag only.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as HTMLElement).closest(".canvas-overlay")) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      const speed = e.ctrlKey ? 0.01 : 0.002; // pinch gestures arrive as ctrl+wheel with small deltas
      setView((v) => {
        const k = clampK(v.k * Math.exp(-dy * speed));
        return { k, x: px - ((px - v.x) * k) / v.k, y: py - ((py - v.y) * k) / v.k };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Drag empty canvas to pan; a click (no movement) in Today opens a new intent there.
  const onPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (e.button !== 0 || target.closest("button, input, select, textarea, a, .card, .canvas-overlay")) return;
    const start = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 4) return;
      if (!moved) setPanning(true);
      moved = true;
      setView((v) => ({ ...v, x: start.vx + ev.clientX - start.x, y: start.vy + ev.clientY - start.y }));
    };
    const up = (ev: PointerEvent) => {
      setPanning(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (moved) return;
      if (composer) { setComposer(null); return; } // a click away closes an open composer
      const rect = ref.current!.getBoundingClientRect();
      const v = viewRef.current;
      const wx = (ev.clientX - rect.left - v.x) / v.k;
      const wy = (ev.clientY - rect.top - v.y) / v.k;
      if (wx >= layout.today.x) openComposer(wx, wy, {});
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  /** Drag a card to rearrange it (shared with everyone); a press without movement opens it. */
  const startCardDrag = (e: React.PointerEvent, c: CardLayout) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest(".card-action")) return;
    const sx = e.clientX;
    const sy = e.clientY;
    const k = viewRef.current.k;
    let moved = false;
    let last = { dx: 0, dy: 0 };
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 4) return;
      moved = true;
      last = { dx: (ev.clientX - sx) / k, dy: (ev.clientY - sy) / k };
      setDrag({ id: c.intent.id, ...last });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (moved) {
        justDragged.current = c.intent.id;
        const to = dragged(c, last.dx, last.dy);
        onMoveIntent(c.intent.id, { dx: Math.round(to.x - c.origin.x), dy: Math.round(to.y - c.origin.y) });
      }
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const reuse = (intent: IntentSummary) => openComposer(layout.nextSlot.x, layout.nextSlot.y, {
    text: intent.text, from: intent.id,
  });

  /** Act on an answer: a composer beside the question, linked to it, optionally pre-filled. */
  const fork = (c: CardLayout, text = "") => openComposer(c.box.x + c.box.w + FORK_GAP, c.box.y, { text, parent: c.intent.id });

  // Where each card is drawn right now (following a drag), for the fork links.
  /** A world position in Today as a card offset ({dx, dy} from Today's day column). */
  const todayOffset = (x: number, y: number) => ({
    dx: Math.round(x - (layout.today.x + BUCKET_PAD)), dy: Math.round(y - layout.head),
  });

  const drawnBox = (c: CardLayout): Box =>
    drag?.id === c.intent.id ? dragged(c, drag.dx, drag.dy)
      : batchDrag && c.intent.batch?.id === batchDrag.id ? dragged(c, batchDrag.dx, batchDrag.dy) : c.box;
  const boxes = new Map(layout.cards.map((c) => [c.intent.id, drawnBox(c)]));
  // Cards entered together (a paste) get one header above them, with actions for the lot.
  const batches = new Map<string, CardLayout[]>();
  for (const c of layout.cards) {
    const id = c.intent.batch?.id;
    if (id) batches.set(id, [...(batches.get(id) ?? []), c]);
  }

  const startBatchDrag = (e: React.PointerEvent, id: string, cards: CardLayout[]) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest("button")) return;
    e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, k = viewRef.current.k;
    let last = { dx: 0, dy: 0 }, moved = false;
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 4) return;
      moved = true;
      last = { dx: (ev.clientX - sx) / k, dy: (ev.clientY - sy) / k };
      setBatchDrag({ id, ...last });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (moved) {
        for (const c of cards) {
          const to = dragged(c, last.dx, last.dy);
          onMoveIntent(c.intent.id, { dx: Math.round(to.x - c.origin.x), dy: Math.round(to.y - c.origin.y) });
        }
      }
      setBatchDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const links: { id: string; from: Box; to: Box; pending?: boolean }[] = [];
  for (const c of layout.cards) {
    const from = c.intent.parent_id ? boxes.get(c.intent.parent_id) : undefined;
    if (from) links.push({ id: c.intent.id, from, to: boxes.get(c.intent.id)! });
  }
  const composerParent = composer?.seed.parent ? boxes.get(composer.seed.parent) : undefined;
  if (composer && composerParent) {
    links.push({ id: "composer", from: composerParent, to: { x: composer.x, y: composer.y, w: CARD_W, h: 48 }, pending: true });
  }

  const zoomBy = (factor: number) => {
    const el = ref.current;
    if (!el) return;
    const px = el.clientWidth / 2;
    const py = el.clientHeight / 2;
    setView((v) => {
      const k = clampK(v.k * factor);
      return { k, x: px - ((px - v.x) * k) / v.k, y: py - ((py - v.y) * k) / v.k };
    });
  };

  const fnLabel = useMemo(() => Object.fromEntries(functions.map((f) => [f.id, f.label])), [functions]);
  const todayCount = layout.cards.filter((c) => bucketOf(new Date(c.intent.created_at), startOfDay(now)) === "today").length;
  const metricsKey = intents.map((i) => i.last_run?.finished_at ?? "").sort().pop() ?? "";

  return (
    <div
      ref={ref}
      className={`canvas${panning ? " panning" : ""}`}
      onPointerDown={onPointerDown}
      style={{
        backgroundSize: `${20 * view.k}px ${20 * view.k}px`,
        backgroundPosition: `${view.x}px ${view.y}px`,
      }}
    >
      <div className="world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
        {layout.buckets.map((b, i) => (
          <section key={b.id} className={`bucket ${b.id}${i % 2 ? " alt" : ""}`} style={pos(b.box)} aria-label={b.label}>
            {b.days.map((d) => (
              <span key={d.key} className="day-label" style={{ left: d.x - b.box.x, top: BAND_EXT + d.y }}>{d.label}</span>
            ))}
          </section>
        ))}

        {[...batches.entries()].map(([id, cards]) => {
          const bx = cards.map((c) => boxes.get(c.intent.id)!);
          const left = Math.min(...bx.map((b) => b.x));
          const top = Math.min(...bx.map((b) => b.y));
          return (
            <BatchHeader key={id} cards={cards.map((c) => c.intent)} box={{ x: left, y: top - BATCH_HEAD_H - 6, w: CARD_W, h: BATCH_HEAD_H }}
                         collapsed={cards.every((c) => collapsed.has(c.intent.id))}
                         onPointerDown={(e) => startBatchDrag(e, id, cards)}
                         onToggleCollapsed={(collapse) => {
                           setCollapsed((cur) => {
                             const next = new Set(cur);
                             for (const c of cards) { if (collapse) next.add(c.intent.id); else next.delete(c.intent.id); }
                             storeCollapsed(boardId, next);
                             return next;
                           });
                         }}
                         boardId={boardId} onDone={onRefresh} notify={notify} />
          );
        })}

        <svg className="fork-links" width={1} height={1} aria-hidden>
          {links.map((l) => <path key={l.id} d={linkPath(l.from, l.to)} className={l.pending ? "pending" : ""} />)}
        </svg>

        {layout.cards.map((c) => {
          const { intent } = c;
          const moving = drag?.id === intent.id;
          const fnId = workTypes[intent.work_type]?.function ?? "";
          const c0 = intent.classification;
          const wtWrites = workTypes[intent.work_type]?.writes;
          const kind: CardKind = intent.kind === "upload" ? "upload"
            : intent.work_type === "question" || wtWrites === false ? "question"
            : wtWrites === true ? "action"
            : intent.text.trim().endsWith("?") ? "question" : "action";   // not classified yet
          return (
            <IntentCard key={intent.id} intent={intent} box={drawnBox(c)}
                        boardId={boardId} kind={kind}
                        selected={intent.id === selectedIntent} moving={moving}
                        collapsed={collapsed.has(intent.id)} onToggleCollapsed={() => toggleCollapsed(intent.id)}
                        detailsOpen={details === intent.id}
                        onToggleDetails={() => setDetails((d) => (d === intent.id ? null : intent.id))}
                        onChanged={onRefresh}
                        fnId={fnId} fnLabel={fnLabel[fnId] ?? ""}
                        wtLabel={c0 ? `${workTypes[intent.work_type]?.label ?? intent.work_type} · inferred (${c0.confidence}): ${c0.rationale}`
                                 : workTypes[intent.work_type]?.label ?? intent.work_type}
                        dimmed={!inMacro(intent, macro, workTypes)}
                        onPointerDown={(e) => startCardDrag(e, c)}
                        onReuse={() => reuse(intent)}
                        onFork={(text) => fork(c, text)}
                        onOpen={() => {
                          if (justDragged.current === intent.id) { justDragged.current = null; return; }
                          onOpenIntent(intent.id);
                        }} />
          );
        })}

        {composer && (
          <div style={{ position: "absolute", left: composer.x, top: composer.y, width: CARD_W }}>
            <InlineComposer
              key={`${composer.x},${composer.y},${composer.seed.from ?? ""}`}
              seed={composer.seed}
              scenario={props.scenario}
              onCancel={() => setComposer(null)}
              onCreateMany={composer.seed.parent ? undefined : async (texts, run) => {
                // Stacked in one column below where the box was, saved with the cards.
                await onCreateIntents(texts, run, texts.map((_, i) =>
                  todayOffset(composer.x, composer.y + BATCH_HEAD_H + 6 + i * (CARD_H + CARD_GAP))));
                setComposer(null);
              }}
              onCreate={async (text) => {
                const parent = composer.seed.parent ? intents.find((i) => i.id === composer.seed.parent) : undefined;
                // The card is created where the box is: it never shows anywhere else first.
                await onCreateIntent(text, true, parent ? { parentId: parent.id, scope: parent.scope } : undefined,
                                     todayOffset(composer.x, composer.y));
                setComposer(null);
              }}
            />
          </div>
        )}
      </div>

      {/* Bucket names stay at the top of the canvas: they follow their band sideways (sticking to
          the left edge while the band is on screen) but never move up or down. */}
      <div className={`bucket-heads${openStack ? " has-open" : ""}`} style={{ top: TOP_INSET - 6 }}>
        {layout.buckets.map((b) => {
          const vw = ref.current?.clientWidth ?? 2400;
          const left = view.x + b.box.x * view.k;
          const right = view.x + (b.box.x + b.box.w) * view.k;
          if (right < 0 || left > vw) return null;
          const list = alertsByBucket.get(b.id);
          const x = Math.min(Math.max(left + 34 * view.k, 14), right - (list?.length ? 300 : 160));
          if (x < left - 40) return null;   // band too narrow on screen to hold its name
          return (
            <div key={b.id} className={`bucket-head fixed ${b.id}${openStack === b.id ? " open" : ""}`} style={{ left: x }}>
              <span className="bucket-label">{b.label}</span>
              {b.id === "today" && (
                <span className="bucket-date">
                  {now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
                  {todayCount === 0 && " · Click anywhere here to add an intent"}
                </span>
              )}
              {b.id === "yesterday" && (
                <span className="bucket-date">{new Date(now.getTime() - DAY_MS).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}</span>
              )}
              {list?.length ? (
                // Alerts belong to their time bucket: pinned beside its name, never moving up or down.
                <div className={`canvas-overlay alert-stack-slot pinned${openStack === b.id ? " open" : ""}`}>
                  <AlertStack boardId={boardId} alerts={list} open={openStack === b.id} workTypes={workTypes}
                              onToggle={() => setOpenStack((cur) => (cur === b.id ? null : b.id))}
                              onChanged={onAlertsChanged}
                              onCreated={(id, ran) => {
                                setOpenStack(null);
                                onRefresh();
                                onAlertsChanged();
                                notify(ran ? `Action card ${id} created and running.` : `Action card ${id} added to Today — review it, then Run.`);
                              }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <MetricsBar macro={macro} refreshKey={metricsKey} />

      <div className="canvas-overlay canvas-tools">
        <button className="btn ghost sm icon-btn" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out">−</button>
        <span className="zoom">{Math.round(view.k * 100)}%</span>
        <button className="btn ghost sm icon-btn" onClick={() => zoomBy(1.2)} aria-label="Zoom in">+</button>
        <button className="btn ghost sm" onClick={focusToday}>Today</button>
        <button className="btn ghost sm" onClick={fit}>Fit</button>
      </div>
    </div>
  );
}

const BATCH_HEAD_H = 30;

/**
 * One line above a batch of cards entered together: what they are, how far they got, and
 * actions for all of them (run the ones not run yet, push or discard what is staged, fold).
 * Drag it to move the whole batch.
 */
function BatchHeader({ cards, box, collapsed, onPointerDown, onToggleCollapsed, boardId, onDone, notify }: {
  cards: IntentSummary[]; box: Box; collapsed: boolean; onPointerDown: (e: React.PointerEvent) => void;
  onToggleCollapsed: (collapse: boolean) => void; boardId: string; onDone: () => void; notify: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const count = (pred: (i: IntentSummary) => boolean) => cards.filter(pred).length;
  const done = count((i) => i.status === "executed");
  const staged = cards.filter((i) => i.status === "staged");
  const asking = count((i) => i.status === "needs_confirmation");
  const working = count((i) => i.status === "running" || i.status === "queued");
  const failed = count((i) => ["failed", "invalid", "interrupted", "cancelled"].includes(i.status));
  const drafts = cards.filter((i) => i.status === "draft");
  const stageIds = staged.flatMap((i) => i.last_run?.staging?.stage_ids ?? []);
  const first = cards.reduce((a, b) => (a.created_at < b.created_at ? a : b));
  const when = new Date(first.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); notify(label); onDone(); } catch (e) { notify((e as Error).message); } finally { setBusy(false); }
  };
  const parts = [
    done && `${done} done`, staged.length && `${staged.length} staged`, working && `${working} running`,
    asking && `${asking} asking`, failed && `${failed} need a look`, drafts.length && `${drafts.length} drafts`,
  ].filter(Boolean);
  return (
    <div className="canvas-overlay batch-head" style={pos(box)} onPointerDown={onPointerDown}
         title="Intents entered together. Drag to move them as one.">
      <span className="grip" aria-hidden />
      <b>Batch · {cards.length}</b>
      <span className="muted">{first.batch?.label || "entered"} {when}{parts.length ? " · " : ""}{parts.join(" · ")}</span>
      <span className="grow" />
      {drafts.length > 0 && (
        <IconButton icon="run" label={`Run the ${drafts.length} draft${drafts.length === 1 ? "" : "s"}`} className="card-action" disabled={busy}
                    onClick={() => act(`Running ${drafts.length} intents.`, () => Promise.all(drafts.map((i) => api.runIntent(boardId, i.id))))} />
      )}
      {stageIds.length > 0 && (
        <>
          <IconButton icon="push" label={`Push all ${staged.length} staged to Kinaxis now`} className="card-action" disabled={busy}
                      onClick={() => act(`Pushing ${staged.length} staged changes.`, () => api.pushStaged(stageIds))} />
          <IconButton icon="discard" label={`Discard all ${staged.length} staged: nothing goes to Kinaxis`} className="card-action" disabled={busy}
                      onClick={() => act(`Discarded ${staged.length} staged changes.`, () => Promise.all(stageIds.map((id) => api.discardStaged(id))))} />
        </>
      )}
      <IconButton icon={collapsed ? "expand" : "minimize"} label={collapsed ? "Expand the batch" : "Collapse the batch"}
                  className="card-action" onClick={() => onToggleCollapsed(!collapsed)} />
    </div>
  );
}

/** A question that has an answer to show. */
function isAnswered(i: IntentSummary): boolean {
  return i.work_type === "question" && i.status === "executed";
}

/** A soft curve from one card to another: right edge to left edge, or the reverse when it sits to the left. */
function linkPath(a: Box, b: Box): string {
  const forward = b.x >= a.x + a.w / 2;
  const x1 = forward ? a.x + a.w : a.x, y1 = a.y + Math.min(a.h / 2, 60);
  const x2 = forward ? b.x : b.x + b.w, y2 = b.y + Math.min(b.h / 2, 40);
  const bend = Math.max(40, Math.abs(x2 - x1) / 2) * (forward ? 1 : -1);
  return "M " + x1 + " " + y1 + " C " + (x1 + bend) + " " + y1 + ", " + (x2 - bend) + " " + y2 + ", " + x2 + " " + y2;
}

function dragged(c: CardLayout, dx: number, dy: number): Box {
  return { ...c.box, x: Math.min(c.maxX, Math.max(c.minX, c.box.x + dx)), y: Math.max(c.minY, c.box.y + dy) };
}

function pos(b: Box): React.CSSProperties {
  return { left: b.x, top: b.y, width: b.w, height: b.h };
}


const moneyCompact = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
function signedMoney(v: number): string {
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${moneyCompact.format(Math.abs(v))}`;
}

/** Colour family a finished card lights up in. */
function toneOf(status: string): string {
  switch (status) {
    case "executed": return "ok";
    case "staged": return "staged";
    case "conversational": return "info";
    case "failed": return "bad";
    case "invalid": case "interrupted": case "cancelled": case "discarded": case "needs_confirmation": return "warn";
    case "queued": case "running": return "busy";
    default: return "idle";
  }
}

type CardKind = "action" | "question" | "upload";

const KIND_TITLE: Record<CardKind, string> = {
  action: "Action: changes the plan in Kinaxis",
  question: "Question: answered read-only, nothing is changed",
  upload: "Mass upload from a file",
};

/** Small line icon saying what kind of card this is. */
function KindIcon({ kind }: { kind: CardKind }) {
  const common = { width: 14, height: 14, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor",
                   strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <span className={`kind-icon kind-${kind}`} title={KIND_TITLE[kind]} aria-label={KIND_TITLE[kind]} role="img">
      {kind === "question" ? (
        <svg {...common}><path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" /><path d="M6.6 5.6a1.4 1.4 0 1 1 2 1.3c-.4.2-.6.5-.6.9" /><path d="M8 9.2v.01" /></svg>
      ) : kind === "upload" ? (
        <svg {...common}><path d="M8 10.5V3" /><path d="M5 6l3-3 3 3" /><path d="M3 11v2h10v-2" /></svg>
      ) : (
        <svg {...common}><path d="M10.5 2.5l3 3-7.5 7.5H3v-3z" /><path d="M9 4l3 3" /></svg>
      )}
    </span>
  );
}

interface CardProps {
  kind: CardKind;
  intent: IntentSummary; box: Box; boardId: string; selected: boolean; dimmed: boolean; moving: boolean;
  collapsed: boolean; detailsOpen: boolean;
  fnId: string; fnLabel: string; wtLabel: string;
  onOpen: () => void; onReuse: () => void; onFork: (text?: string) => void; onPointerDown: (e: React.PointerEvent) => void;
  onToggleCollapsed: () => void; onToggleDetails: () => void; onChanged: () => void;
}

function IntentCard(props: CardProps) {
  const { kind, intent, box, selected, dimmed, moving, collapsed, detailsOpen, fnId, fnLabel, wtLabel,
          onOpen, onReuse, onFork, onPointerDown, onToggleCollapsed, onToggleDetails, onChanged } = props;
  const busy = intent.status === "running" || intent.status === "queued";
  const asking = intent.status === "needs_confirmation";
  const last = intent.last_run;
  // $ only for changes that reached Kinaxis (Done) or are queued to (Staged, shown as pending).
  const written = intent.status === "executed" || intent.status === "staged";
  const value = written ? last?.value_delta ?? null : null;
  const pending = intent.status === "staged";
  const skus = written ? last?.sku_count ?? null : null;
  const tone = toneOf(intent.status);
  const answered = isAnswered(intent);
  const question = intent.work_type === "question";
  const status = answered ? "Answered" : STATUS_LABEL[intent.status] ?? intent.status;
  const cls = `card tone-${tone}${busy ? " busy" : ""}${asking ? " asking" : ""}${selected ? " selected" : ""}${dimmed ? " dimmed" : ""}${moving ? " moving" : ""}`;
  const label = `Intent ${intent.id}: ${intent.text}. ${status}${value ? `. Plan value ${signedMoney(value)}` : ""}${skus ? `. ${skus} SKUs` : ""}`;
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

  // Collapsed: a thin outlined box with the start of the text (cut to fit) and the $ value.
  if (collapsed && !busy && !asking) {
    return (
      <div role="button" tabIndex={0} className={`${cls} collapsed`} style={pos(box)} title={intent.text}
           aria-label={label} onPointerDown={onPointerDown} onClick={onToggleCollapsed}
           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleCollapsed(); } }}>
        <KindIcon kind={kind} />
        <span className="collapsed-text">{intent.text}</span>
        <span className={`collapsed-value${value ? (value > 0 ? " up" : " down") : ""}${pending ? " pending" : ""}`}>
          {value ? signedMoney(value) : status}</span>
      </div>
    );
  }

  return (
    <>
      <div role="button" tabIndex={0} className={cls} style={pos(box)} aria-label={label}
           onPointerDown={onPointerDown} onClick={onOpen}
           onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}>
        <div className="card-top">
          <span className="grip" aria-hidden title="Drag to move" />
          <KindIcon kind={kind} />
          {busy ? (
            <span className="working"><span className="working-dots" aria-hidden><i /><i /><i /></span>
              {intent.work_type === "auto" ? "Understanding…" : "Working…"}</span>
          ) : (
            <span className="card-status">{status}</span>
          )}
          <span className="grow" />
          {value ? <span className={`card-value ${value > 0 ? "up" : "down"}${pending ? " pending" : ""}`}
                        title={pending ? "Staged: not in Kinaxis until it is pushed" : "Change in demand-plan value (units × consensus unit price)"}>
            {signedMoney(value)}</span> : null}
          {intent.origin?.kind === "alert" && (
            <span className="from-alert" title={`From alert: ${intent.origin.rule_name}`}><BellIcon size={12} /></span>
          )}
          {intent.sample && <span className="sample-tag">Sample</span>}
          {intent.kind === "upload" && <span className="sample-tag" title="Mass upload from a file">Upload</span>}
        </div>
        <div className="card-text">{intent.text}</div>
        {answered && last?.summary && <SummaryText text={last.summary} compact />}
        {answered && (last?.suggestions?.length ?? 0) > 0 && (
          <div {...contain}><Suggestions items={last!.suggestions!} onPick={(t) => onFork(t)} /></div>
        )}
        {asking && <ScopeConfirm intent={intent} boardId={props.boardId} onDone={onChanged} />}
        {pending && last?.staging && <StagedBar staging={last.staging} onDone={onChanged} />}
        {!busy && !asking && (
          <div className="card-foot">
            {skus ? <span className="nowrap">{skus} SKU{skus === 1 ? "" : "s"}</span> : null}
            {fnLabel && <span className={`fn-tag fn-${fnId}`} title={wtLabel}><span className="fn-dot" />{fnLabel}</span>}
            <span className="grow" />
            <IconButton icon={detailsOpen ? "collapse" : "expand"} label={detailsOpen ? "Hide details" : "Details"}
                        className="card-action" expanded={detailsOpen} onClick={stop(onToggleDetails)} />
            {answered && <IconButton icon="fork" label="Act on this: turn the answer into an intent, linked to the question"
                                     className="card-action" onClick={stop(() => onFork())} />}
            {intent.kind !== "upload" && (
              <IconButton icon="reuse" label="Reuse this text in Today" className="card-action" onClick={stop(onReuse)} />
            )}
            <IconButton icon="minimize" label="Collapse" className="card-action" onClick={stop(onToggleCollapsed)} />
          </div>
        )}
        {busy && <div className="progress" aria-hidden />}
      </div>
      {detailsOpen && (question
        ? <div className="canvas-overlay card-details" style={{ left: box.x, top: box.y + box.h + 6, width: Math.max(box.w, CARD_W) }} {...contain}>
            <AnswerPanel boardId={props.boardId} intentId={intent.id} refreshKey={last?.finished_at} onFork={(t) => onFork(t)} />
          </div>
        : <CardDetails intent={intent} boardId={props.boardId} box={box} onAct={(t) => onFork(t)} />)}
    </>
  );
}

/** Keep clicks and keys inside a card control from opening, dragging or zooming the card. */
const contain = {
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onClick: (e: React.MouseEvent) => e.stopPropagation(),
  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
};

function candidateScope(base: Scope, picks: ScopeCandidate[]): Scope {
  const scope: Scope = { family: base.family, group1: base.group1, group2: base.group2 ?? "", skus: [...base.skus] };
  for (const c of picks) {
    if (c.level === "family" || c.level === "group1" || c.level === "group2") scope[c.level] = c.value;
    else if (!scope.skus.includes(c.value)) scope.skus.push(c.value);
  }
  return scope;
}

/** "Which products did you mean?": one pick per unclear word, or search for something else. */
function ScopeConfirm({ intent, boardId, onDone }: { intent: IntentSummary; boardId: string; onDone: () => void }) {
  const q = intent.last_run?.scope_question;
  const mentions = (typeof q === "object" && q?.mentions) || intent.classification?.scope_resolution?.mentions || [];
  const open = mentions.filter((m) => m.status !== "resolved");
  const [picks, setPicks] = useState<Record<number, ScopeCandidate>>({});
  const [search, setSearch] = useState("");
  const [found, setFound] = useState<ScopeCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (search.trim().length < 2) { setFound([]); return; }
    const t = window.setTimeout(() => api.scopeSearch(search.trim()).then((r) => setFound(r.matches.slice(0, 6))).catch(() => setFound([])), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const resolved = mentions.filter((m) => m.status === "resolved").map((m) => m.candidates[0]).filter(Boolean);
  const chosen = Object.values(picks);
  const ready = open.length === 0 || open.every((_, n) => picks[n]);
  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      await api.confirmScope(boardId, intent.id, candidateScope(intent.scope, [...resolved, ...chosen]));
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const fallback = typeof q === "string" ? q : "";
  const unscoped = typeof q === "object" && q?.status === "unscoped";
  const allCount = typeof q === "object" ? q?.sku_count : undefined;

  return (
    <div className="scope-confirm" {...contain}>
      {open.length === 0 && fallback && <div className="small ink2">{fallback}</div>}
      {unscoped && (
        <div className="small ink2">
          No products recognised: this would change <b>all {allCount ?? ""} SKUs</b>. Search for the products, or run on all.
        </div>
      )}
      {open.map((m, n) => (
        <div key={`${m.text}-${n}`} className="scope-q">
          <span className="small ink2">
            “{m.text}”{m.status === "not_found" ? " — not found" : m.status === "outside_filters" ? " — outside your filters" : ""}
          </span>
          <div className="chip-row">
            {m.candidates.slice(0, 4).map((c) => (
              <button key={`${c.level}:${c.value}`} type="button" title={c.label}
                      className={`pick${picks[n]?.value === c.value && picks[n]?.level === c.level ? " on" : ""}`}
                      onClick={() => setPicks((p) => ({ ...p, [n]: c }))}>
                {c.value}<span className="n">{c.sku_count}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <input className="pick-search" placeholder="Something else…" value={search} onChange={(e) => setSearch(e.target.value)}
             aria-label="Search products" />
      {found.length > 0 && (
        <div className="chip-row">
          {found.map((c) => (
            <button key={`${c.level}:${c.value}`} type="button" title={c.label} className="pick"
                    onClick={() => { setPicks((p) => ({ ...p, [Math.max(0, open.findIndex((_, n) => !p[n]))]: c })); setSearch(""); }}>
              {c.value}<span className="n">{c.sku_count}</span>
            </button>
          ))}
        </div>
      )}
      {error && <div className="small error-text">{error}</div>}
      <div className="row">
        <span className="small muted grow">{chosen.length ? scopeLabel(candidateScope(intent.scope, [...resolved, ...chosen])) : "Pick what you meant"}</span>
        <button className="btn primary sm" disabled={!ready || busy} onClick={confirm}>
          {busy ? "Starting…" : unscoped && !chosen.length ? `Run on all${allCount ? ` ${allCount}` : ""}` : "Run"}
        </button>
      </div>
    </div>
  );
}

/** Staged write-back: counts down to the automatic push; push now or discard. */
function StagedBar({ staging, onDone }: { staging: NonNullable<RunSummary["staging"]>; onDone: () => void }) {
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const ids = staging.stage_ids ?? [];
  const left = staging.push_at ? Math.max(0, Math.round((new Date(staging.push_at).getTime() - now) / 1000)) : null;
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try { await fn(); onDone(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const label = staging.status === "pushing" ? "Pushing to Kinaxis…"
    : left == null ? "Staged" : left === 0 ? "Pushing…" : `Pushes in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  return (
    <div className="staged-bar" {...contain}>
      <span className="small">{label}</span>
      <span className="grow" />
      {staging.status === "pending" && (
        <>
          <IconButton icon="discard" label="Discard: nothing goes to Kinaxis" className="btn ghost sm" disabled={busy}
                      onClick={() => act(() => Promise.all(ids.map((id) => api.discardStaged(id))))} />
          <IconButton icon="push" label="Push to Kinaxis now" className="btn sm" disabled={busy}
                      onClick={() => act(() => api.pushStaged(ids))} />
        </>
      )}
      {error && <span className="small error-text">{error}</span>}
    </div>
  );
}

/** Drill-down under a card: before vs after for the scope (staged or pushed), and the biggest SKU moves. */
function CardDetails({ intent, boardId, box, onAct }: { intent: IntentSummary; boardId: string; box: Box; onAct: (text: string) => void }) {
  const [ba, setBa] = useState<BeforeAfter | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setBa(null);
    api.beforeAfter(boardId, intent.id).then((d) => live && setBa(d)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [boardId, intent.id, intent.last_run?.finished_at, intent.status]);

  const last = intent.last_run;
  const months = ba?.months ?? [];
  const skuRows = (ba?.by_sku ?? []).slice()
    .sort((a, b) => Math.abs(b.delta_value ?? b.delta) - Math.abs(a.delta_value ?? a.delta)).slice(0, 8);
  const t = ba?.totals;
  return (
    <div className="canvas-overlay card-details" style={{ left: box.x, top: box.y + box.h + 6, width: Math.max(box.w, CARD_W) }}
         {...contain}>
      {last?.summary && <SummaryText text={last.summary} onAct={onAct} />}
      {error ? <div className="small error-text">{error}</div>
        : !ba ? <div className="row small muted"><div className="spinner" /> Reading from Kinaxis…</div>
        : ba.state === "none" || !months.length ? <div className="small muted">Nothing written to Kinaxis.</div>
        : (
          <>
            <div className="ba-head small">
              <b>{ba.measure === "actuals" ? "Actuals" : "Consensus"}</b>
              <span className="muted">{ba.state === "staged" ? "after = if pushed" : `in ${ba.scenario}`}{ba.recalculating ? " · Kinaxis recalculating" : ""}</span>
              <span className="grow" />
              {t && <span>{fmtSigned(t.delta)} units{t.delta_value != null ? ` · ${signedMoney(t.delta_value)}` : ""}</span>}
            </div>
            <LineChart height={150} labels={months.map((m) => m.month)} highlight={months.map((m) => m.changed)} series={[
              { name: "Before", color: "var(--series-base)", dashed: true, values: months.map((m) => m.before) },
              { name: ba.state === "staged" ? "After (staged)" : "After", color: "var(--series-target)", values: months.map((m) => m.after) },
            ]} />
          </>
        )}
      {skuRows.length > 0 && (
        <table className="data compact">
          <thead><tr><th>SKU</th><th className="num">Before</th><th className="num">After</th><th className="num">$</th></tr></thead>
          <tbody>{skuRows.map((r) => (
            <tr key={r.sku}><td className="mono">{r.sku}</td><td className="num">{fmtUnits(r.before)}</td><td className="num">{fmtUnits(r.after)}</td>
              <td className="num">{r.delta_value != null ? signedMoney(r.delta_value) : fmtSigned(r.delta)}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
